import { AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ElementRef, ViewChild } from '@angular/core';
import { CwsPlanCategoryCodeComboBoxModel } from './cws-plan-category-code-combo-box.model';
import { GetCwsPlanCategoryListService } from './api-services/get-cws-plan-category-list.service';
import { PlessHttp } from '../../../../shared/http/pless-http.service';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { PlessViewUtilsSub } from '../../../../commons/kymw00/pless-view-utils-sub.service';
import { PlessDialogServiceSub } from '../../../../commons/kymw00/pless-dialog-service-sub';

/**
 * 共通部品 EC商品企画カテゴリコンボボックスを制御するクラス
 *
 * @export CwsPlanCategoryCodeComboBoxComponent
 * @class CwsPlanCategoryCodeComboBoxComponent
 * @implements {OnInit}
 * @implements {AfterViewInit}
 * @implements {OnChanges}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-cws-plan-category-code-combo-box',
  templateUrl: 'cws-plan-category-code-combo-box.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: CwsPlanCategoryCodeComboBoxComponent,
      multi: true
    },
    {
      provide: GetCwsPlanCategoryListService,
      useFactory: (plessHttp: PlessHttp, dialogService: PlessDialogServiceSub) => {
        return new GetCwsPlanCategoryListService(plessHttp, dialogService);
      },
      deps: [PlessHttp, PlessDialogServiceSub]
    }
  ],
})

export class CwsPlanCategoryCodeComboBoxComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {
  /**
   * Creates an instance of CwsPlanCategoryCodeComboBoxComponent.
   * @param {GetCwsPlanCategoryListService} getCwsPlanCategoryList
   * @memberof CwsPlanCategoryCodeComboBoxComponent
   */
  constructor(
    public elementRef: ElementRef,
    private getCwsPlanCategoryList: GetCwsPlanCategoryListService,
  ) { }

  @ViewChild('comboBox', { static: true }) comboBox: ElementRef;

  @Input() public subid: string = null;
  @Input() blank: boolean = <boolean>true;                                      // 空白フラグ
  // @Input() orderCode: string = <string>'';
  @Input() public initFocusItem: any = null;                                    // ロード完了後フォーカスを移動させるid
  @Input() public categoryLevel = 1;
  @Input() public stockLimitFlg = <string>'0';                                  // 在庫限定フラグ(0：共同購入、1：在庫限定)
  @Input() public tabindex: number = null;
  @Input() forcedEnabledMode: string = null;

  @Input() disabled: boolean = <boolean>false;
  @Input() required: boolean = <boolean>false;
  @Input() isInitCodeDiv: string = null;
  @Input() initAutoLoad: boolean = <boolean>true;
  @Input() left: number = <number>10;
  @Input() top: number = <number>10;
  @Input() width: number | string;
  @Input() height: number = <number>20;
  @Input() visible: boolean = <boolean>true;                                    // 表示・非表示フラグ

  /** ngModel連携用 */
  @Input() public selectedValue: string = null;

  public selectedOrderCode: string = <string>'';                                // 受注コード
  public selectedCategoryCode: string = <string>'';                             // カテゴリコード
  public selectedCategoryName: string = <string>'';                             // カテゴリ名

  public selectedIndex: number = <number>-1;
  public model: CwsPlanCategoryCodeComboBoxModel = new CwsPlanCategoryCodeComboBoxModel();

  // ValueAccessor
  public onChange(_: any) { }
  public onTouched() { }
  public writeValue(text: string): void {
    if (text !== this.selectedValue) {
      this.selectedValue = text;
      this.setCategoryCode(text);
    }
  }
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * 初期化処理
   *
   * @memberof CwsPlanCategoryCodeComboBoxComponent
   */
  ngOnInit(): void {
    // コンボボックスインスタンス設定
    this.model.component = this;
    this.comboBox.nativeElement.setAttribute('id', this.subid + '_sub');
    this.comboBox.nativeElement.setAttribute('forcedEnabledMode', this.forcedEnabledMode);
  }

  /**
   * コンポーネント終了時
   *
   * @memberof CwsPlanCategoryCodeComboBoxComponent
   */
  ngOnDestroy(): void {
    this.selectedOrderCode = null;
    this.selectedCategoryCode = null;
    this.selectedCategoryName = null;
    this.model = null;
  }

  /**
   * ロード完了時
   *
   * @memberof CwsPlanCategoryCodeComboBoxComponent
   */
  ngAfterViewInit(): void {
  }

  /**
  * 値変更時イベント
  *
  * @param {SimpleChanges} changes 変更イベント
  * @memberof SmallClassCodeComboBoxComponent
  */
  ngOnChanges(changes: SimpleChanges): void {
    Object.keys(changes).forEach((propName) => {
      switch (propName) {
        case 'blank':
          // 先頭空白表示フラグ
          this.model.blank = changes.blank.currentValue;
          break;
        case 'initAutoLoad':
          // 初期データ自動取得フラグ
          this.model.initAutoLoad = changes.initAutoLoad.currentValue;
          break;
      }
    });
  }

  /**
   * コンボボックスの選択カテゴリコードを切り替える
   *
   * @param {string} categoryCode カテゴリコード
   * @memberof CwsPlanCategoryCodeComboBoxComponent
   */
  public setCategoryCode(categoryCode: string): void {
    this.model.setCategoryCode = categoryCode;

    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedCwsPlanCategoryCode();
    }
  }

  /**
   * コンボボックスの選択コードNameを切り替える
   *
   * @memberof CwsPlanCategoryCodeComboBoxComponent
   */
  public updateSelectedCwsPlanCategoryCode(): void {
    const cwsPlanCategoryList: Array<any> = this.model.cwsPlanCategoryList;
    for (let i = Number(0); i < cwsPlanCategoryList.length; i++) {
      if (PlessViewUtilsSub.trim(cwsPlanCategoryList[i].CategoryCode) === this.model.setCategoryCode) {
        this.selectedIndex = i;
        this.update();
      }
    }
    if (this.initFocusItem != null) {
      let queryedElement;
      if (this.initFocusItem instanceof HTMLElement) {
        queryedElement = this.initFocusItem.querySelector('textarea, input, select');
      } else {
        queryedElement = this.initFocusItem.elementRef.nativeElement.querySelector('textarea, input, select');
      }
      (queryedElement ? <HTMLElement>queryedElement : this.initFocusItem).focus();
    }
  }

  /**
   * 再ロード
   *
   * @param {*} [e=null]
   * @returns {void}
   * @memberof CwsPlanCategoryCodeComboBoxComponent
   */
  public update(e: any = null): void {
    if (e != null) {
      this.selectedIndex = e.target.selectedIndex;
    }
    if (this.selectedIndex === -1) {
      this.selectedCategoryCode = <string>'';
      this.selectedCategoryName = <string>'';
      return;
    }

    this.selectedCategoryCode = this.model.cwsPlanCategoryList[this.selectedIndex].CategoryCode;
    this.selectedCategoryName = this.model.cwsPlanCategoryList[this.selectedIndex].CategoryName;

    this.model.setCategoryCode = '';
  }

  /**
   * 初期化処理
   *
   * @memberof CwsPlanCategoryCodeComboBoxComponent
   */
  public clear(): void {
    this.selectedOrderCode = <string>'';
    this.selectedCategoryCode = <string>'';
    this.selectedCategoryName = <string>'';
    this.selectedIndex = <number>-1;

    this.model = new CwsPlanCategoryCodeComboBoxModel();
    this.model.component = this;
    this.model.blank = this.blank;
  }

  /**
   * データ取得
   *
   * @memberof CwsPlanCategoryCodeComboBoxComponent
   */
  public updateCategoryList(): void {
    this.getCwsPlanCategoryList.execute(this.model, this.selectedOrderCode,
      String(this.categoryLevel), this.stockLimitFlg);
  }

  /**
   * コンボボックスの選択Indexを切り替える
   *
   * @param {number} index
   * @memberof CwsPlanCategoryCodeComboBoxComponent
   */
  public setSelectedIndex(index: number): void {
    this.model.setSelectedIndex = index;
    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedIndex();
    }
  }

  /**
   * コンボボックスの選択Indexを切り替える
   *
   * @memberof CwsPlanCategoryCodeComboBoxComponent
   */
  public updateSelectedIndex(): void {
    if (this.model.setSelectedIndex >= 0) {
      this.selectedIndex = this.model.setSelectedIndex;
      this.model.setSelectedIndex = <number>-1;
      this.update(null);
    }
  }

}
